import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import BottomSheetContent from '../BottomSheetComponents/BottomSheetContent'
import ComparisonBarGraphForRebalancing from '../graphs/ComparisonBarGraphForRebalancing'
import { BigLeafRank } from '../know-why/score-sections/BigLeafRank'
import { ReasonsToSwitch } from '../../types/rebalance'
import { NumberedItem } from '../ui-components/NumberedItem'
import { GradientStrip, GradientStripType } from '../ui-components/GradientStrip'
import { RegularPlanAlert } from '../BottomSheetComponents/RegularPlanAlert'
import MetaDataProvider from '../../state-management/providers/MetaDataProvider'

const BetterReturnsListItem = ({ reasonsToSwitch, number }: { reasonsToSwitch: ReasonsToSwitch; number: number }) => {
  return (
    <NumberedItem number={number} title='Better returns' subtitle='than your current fund'>
      <GradientStrip type={GradientStripType.GREEN}>
        <div className='text-lg font-medium pb-1'>+{reasonsToSwitch.xirrDiff.toFixed(2)}% XIRR</div>
        <div className='text-13'>
          Higher returns in the last {reasonsToSwitch.xirrDiffDurationInMonths}+ months, and is expected to maintain
          better performance
        </div>
      </GradientStrip>
    </NumberedItem>
  )
}

const ExcellentPowerRankListItem = ({ powerRank, number }: { powerRank: number; number: number }) => {
  return (
    <NumberedItem number={number} title='Excellent Power Rank' subtitle='in its category'>
      <GradientStrip type={GradientStripType.GREEN}>
        <div className='text-lg font-medium pb-1'>
          <BigLeafRank isElite={true} powerRank={powerRank} categoryCount={null} powerForm='IN_FORM' />
        </div>
        <div className='text-13'>Top ranked, outperforming the benchmark and your current fund</div>
      </GradientStrip>
    </NumberedItem>
  )
}

const MissedGainsListItem = ({
  fundName,
  missedGains,
  currentWealth,
  switchFundName,
  missedGainsAsOfDate,
  number
}: {
  fundName?: string
  missedGains?: number
  currentWealth: number
  switchFundName?: string
  missedGainsAsOfDate?: number
  number: number
}) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const handleInfoClick = () => {
    openBottomSheet(
      <BottomSheetContent
        headingComponent={
          <div className='px-5'>
            <div className='text-grey10 text-[22px] font-normal mb-4'>
              {roundOffAndFormatNumberWithCurrency(missedGains)} missed gains
            </div>
          </div>
        }
        onDismiss={closeBottomSheet}
      >
        <div className=''>
          <div className='text-sm text-grey40 mb-4'>
            This means your investment could have earned {roundOffAndFormatNumberWithCurrency(missedGains)} more, had
            you invested in {switchFundName}. Calculated as of {convertEpochToDate(missedGainsAsOfDate)}
          </div>
          <div className='h-64 w-full'>
            <ComparisonBarGraphForRebalancing
              switchFundName={switchFundName}
              fundName={fundName}
              rebalanceFundName={switchFundName}
              regularValue={currentWealth}
              directValue={currentWealth + missedGains}
            />
          </div>
        </div>
      </BottomSheetContent>
    )
  }

  if (missedGains > 0) {
    return (
      <NumberedItem number={number} title='Missed Gains' subtitle='on your current fund'>
        <GradientStrip type={GradientStripType.ORANGE}>
          <div className='text-lg font-medium pb-1 flex flex-row items-center'>
            <span>+ {roundOffAndFormatNumberWithCurrency(missedGains)}</span>
            <span className='ml-2 mt-[2px] cursor-pointer' onClick={handleInfoClick}>
              <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <g clipPath='url(#clip0_43745_3497)'>
                  <path
                    d='M8 0.5C10.5717 0.5 12.4232 1.1537 13.6348 2.36523C14.8463 3.57678 15.5 5.42834 15.5 8C15.5 10.5717 14.8463 12.4232 13.6348 13.6348C12.4232 14.8463 10.5717 15.5 8 15.5C5.42834 15.5 3.57678 14.8463 2.36523 13.6348C1.1537 12.4232 0.5 10.5717 0.5 8C0.5 5.42834 1.15369 3.57678 2.36523 2.36523C3.57678 1.15369 5.42834 0.5 8 0.5Z'
                    fill='white'
                    stroke='#FFE2BF'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M6.37667 10.2069C6.00306 10.2069 5.7002 10.5023 5.7002 10.8667C5.7002 11.2312 6.00306 11.5266 6.37667 11.5266H9.62372C9.99733 11.5266 10.3002 11.2312 10.3002 10.8667C10.3002 10.5023 9.99733 10.2069 9.62372 10.2069H8.67667V7.17164C8.67667 6.80722 8.3738 6.51181 8.0002 6.51181H6.91784C6.54424 6.51181 6.24137 6.80722 6.24137 7.17164C6.24137 7.53606 6.54424 7.83148 6.91784 7.83148H7.32372V10.2069H6.37667ZM9.08255 4.5323C9.08255 5.11536 8.59796 5.58804 8.0002 5.58804C7.40243 5.58804 6.91784 5.11536 6.91784 4.5323C6.91784 3.94924 7.40243 3.47656 8.0002 3.47656C8.59796 3.47656 9.08255 3.94924 9.08255 4.5323Z'
                    fill='#CC5C2D'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_43745_3497'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            </span>
          </div>
          <div className='text-13'>
            Returns you missed by staying in your current fund instead of switching earlier. Calculated as of{' '}
            {convertEpochToDate(missedGainsAsOfDate)}
          </div>
        </GradientStrip>
      </NumberedItem>
    )
  }

  return (
    <NumberedItem number={number} title='Current fund is consistently' subtitle='underperforming in its category'>
      <GradientStrip type={GradientStripType.ORANGE}>
        <div className='text-13'>Staying invested could weaken your portfolio's long-term performance.</div>
      </GradientStrip>
    </NumberedItem>
  )
}

const HighExposureFundListItem = ({
  highExposureFundPercentage,
  number
}: {
  highExposureFundPercentage?: number
  number: number
}) => {
  return (
    <NumberedItem
      number={number}
      title={`This fund carries high exposure (concentration risk) in your portfolio.`}
      subtitle={`We calculate a fund's exposure by considering both its weightage in your portfolio and the current value invested in it. Spreading your investments across multiple funds can help reduce the risk associated with a single fund's performance.`}
    >
      <div></div>
    </NumberedItem>
  )
}

const ExtraCommissionsListItem = ({
  additionalCommissionsPaid,
  number,
  isin,
}: {
  additionalCommissionsPaid?: number
  number: number
  isin?: string
}) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const handleInfoClick = () => {
    // Use directFundISIN if available, otherwise fall back to regular fund isin
    openBottomSheet(
      <MetaDataProvider isin={isin} from='OTHER'>
        <RegularPlanAlert
          onDismiss={closeBottomSheet}
          ScreenName='ReasonsToRebalance'
        />
      </MetaDataProvider>
    )
  }

  return (
    <NumberedItem number={number} title='Higher expense ratio' subtitle='on your current fund'>
      <GradientStrip type={GradientStripType.ORANGE}>
        <div className='text-lg font-medium pb-1 flex flex-row items-center'>
          <span>+ {roundOffAndFormatNumberWithCurrency(additionalCommissionsPaid)}</span>
          <span className='ml-2 mt-[2px] cursor-pointer' onClick={handleInfoClick}>
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <g clipPath='url(#clip0_43745_3497)'>
                <path
                  d='M8 0.5C10.5717 0.5 12.4232 1.1537 13.6348 2.36523C14.8463 3.57678 15.5 5.42834 15.5 8C15.5 10.5717 14.8463 12.4232 13.6348 13.6348C12.4232 14.8463 10.5717 15.5 8 15.5C5.42834 15.5 3.57678 14.8463 2.36523 13.6348C1.1537 12.4232 0.5 10.5717 0.5 8C0.5 5.42834 1.15369 3.57678 2.36523 2.36523C3.57678 1.15369 5.42834 0.5 8 0.5Z'
                  fill='white'
                  stroke='#FFE2BF'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M6.37667 10.2069C6.00306 10.2069 5.7002 10.5023 5.7002 10.8667C5.7002 11.2312 6.00306 11.5266 6.37667 11.5266H9.62372C9.99733 11.5266 10.3002 11.2312 10.3002 10.8667C10.3002 10.5023 9.99733 10.2069 9.62372 10.2069H8.67667V7.17164C8.67667 6.80722 8.3738 6.51181 8.0002 6.51181H6.91784C6.54424 6.51181 6.24137 6.80722 6.24137 7.17164C6.24137 7.53606 6.54424 7.83148 6.91784 7.83148H7.32372V10.2069H6.37667ZM9.08255 4.5323C9.08255 5.11536 8.59796 5.58804 8.0002 5.58804C7.40243 5.58804 6.91784 5.11536 6.91784 4.5323C6.91784 3.94924 7.40243 3.47656 8.0002 3.47656C8.59796 3.47656 9.08255 3.94924 9.08255 4.5323Z'
                  fill='#CC5C2D'
                />
              </g>
              <defs>
                <clipPath id='clip0_43745_3497'>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </span>
        </div>
        <div className='text-13'>Returns you could&apos;ve made if you were invested in the Direct plan of this fund</div>
      </GradientStrip>
    </NumberedItem>
  )
}

// Component configuration factory
const createComponentConfig = (props: {
  fundName?: string
  reasonsToSwitch?: ReasonsToSwitch
  powerRank?: number
  missedGains?: number
  isHighExposureFund?: boolean
  highExposureFundPercentage?: number
  currentWealth: number
  switchFundName?: string
  missedGainsAsOfDate?: number
  isRegularFund: boolean
  additionalCommissionsPaid?: number
  isin?: string
  directFundISIN?: string
}) => [
    {
      condition: props.isHighExposureFund,
      component: (number: number) => (
        <HighExposureFundListItem
          key='high-exposure'
          highExposureFundPercentage={props.highExposureFundPercentage}
          number={number}
        />
      )
    },
    {
      condition: props?.reasonsToSwitch?.xirrDiff,
      component: (number: number) => (
        <BetterReturnsListItem key='better-returns' reasonsToSwitch={props.reasonsToSwitch} number={number} />
      )
    },
    {
      condition: !props.isRegularFund && true, // Always show power rank
      component: (number: number) => (
        <ExcellentPowerRankListItem key='power-rank' powerRank={props.powerRank} number={number} />
      )
    },
    {
      condition: props?.missedGains && props.missedGains > 0, // Always show missed gains (handles both cases internally)
      component: (number: number) => (
        <MissedGainsListItem
          key='missed-gains'
          fundName={props.fundName}
          missedGains={props.missedGains}
          currentWealth={props.currentWealth}
          switchFundName={props.switchFundName}
          missedGainsAsOfDate={props.missedGainsAsOfDate}
          number={number}
        />
      )
    },
    {
      condition: props.isRegularFund && props?.additionalCommissionsPaid && props.additionalCommissionsPaid > 0,
      component: (number: number) => (
        <ExtraCommissionsListItem
          key='extra-commissions'
          additionalCommissionsPaid={props?.additionalCommissionsPaid}
          number={number}
          isin={props.isin}
          directFundISIN={props.directFundISIN}
        />
      )
    }
  ]

const ReasonsToRebalance = ({
  fundName,
  reasonsToSwitch,
  powerRank,
  missedGains,
  isHighExposureFund,
  highExposureFundPercentage,
  currentWealth,
  switchFundName,
  missedGainsAsOfDate,
  isRegularFund = false,
  additionalCommissionsPaid = 0,
  isin,
  directFundISIN
}: {
  fundName?: string
  reasonsToSwitch?: ReasonsToSwitch
  powerRank?: number
  missedGains?: number
  isHighExposureFund?: boolean
  highExposureFundPercentage?: number
  currentWealth: number
  switchFundName?: string
  missedGainsAsOfDate?: number
  isRegularFund?: boolean
  additionalCommissionsPaid?: number
  isin?: string
  directFundISIN?: string
}) => {
  // Get component configuration
  const componentsToRender = createComponentConfig({
    fundName: fundName ?? '',
    reasonsToSwitch,
    powerRank,
    missedGains,
    isHighExposureFund,
    highExposureFundPercentage,
    currentWealth,
    switchFundName,
    missedGainsAsOfDate,
    isRegularFund,
    additionalCommissionsPaid,
    isin,
    directFundISIN
  })

  // Filter and render components with proper numbering
  const visibleComponents = componentsToRender
    .filter((item) => item.condition)
    .map((item, index) => item.component(index + 1))

  return (
    visibleComponents.length > 0 && <div className='pt-8'>
      <h2 className='text-lg px-5'>{isRegularFund ? 'Reasons to switch' : 'Reasons to rebalance'}</h2>
      <div className='flex flex-col'>{visibleComponents}</div>
    </div>
  )
}

export default ReasonsToRebalance
